import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Truck, Phone, RefreshCw, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Custom car icon (driver) — uses an emoji-style SVG
const carIcon = L.divIcon({
  className: "",
  iconSize: [48, 48],
  iconAnchor: [24, 24],
  html: `<div style="font-size:36px;line-height:1;filter:drop-shadow(0 4px 6px rgba(0,0,0,0.4));">🚚</div>`,
});

const homeIcon = L.divIcon({
  className: "",
  iconSize: [48, 48],
  iconAnchor: [24, 48],
  html: `
    <div style="position:relative;text-align:center;">
      <div style="background:#E85D45;color:white;border-radius:50%;width:36px;height:36px;display:flex;align-items:center;justify-content:center;margin:0 auto;border:3px solid white;box-shadow:0 4px 8px rgba(0,0,0,0.3);">
        <span style="font-size:18px;">🏪</span>
      </div>
    </div>`,
});

const statusLabels = {
  pending: "قيد المعالجة",
  confirmed: "تم التأكيد",
  assigned: "تم تعيين سائق",
  accepted: "السائق وافق",
  preparing: "جاري التجهيز",
  ready: "تم التجهيز",
  on_the_way: "🚚 السائق في الطريق إليك",
  awaiting_payment_confirmation: "بانتظار تأكيد الدفع",
  delivered: "✅ تم التوصيل",
  delivering: "قيد التوصيل",
};

const OrderTracking = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingLocation, setSavingLocation] = useState(false);
  const intervalRef = useRef(null);

  const fetchTracking = async () => {
    try {
      const { data } = await axios.get(`${API}/orders/${orderId}/tracking`);
      setTracking(data);
      // Stop polling when order is delivered — and auto-redirect after a beat
      if (data?.status === "delivered" && intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل تحميل بيانات التتبّع");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTracking();
    intervalRef.current = setInterval(fetchTracking, 8000);
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line
  }, [orderId]);

  const captureMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("متصفحك لا يدعم تحديد الموقع");
      return;
    }
    setSavingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await axios.post(`${API}/me/location`, {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          });
          toast.success("تم حفظ موقعك");
          fetchTracking();
        } catch {
          toast.error("فشل حفظ الموقع");
        } finally {
          setSavingLocation(false);
        }
      },
      () => {
        toast.error("لم نتمكن من الحصول على موقعك. تأكّد من الإذن.");
        setSavingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#3B82F6]" />
      </div>
    );
  }

  const customerHasCoords =
    tracking?.customer?.lat != null && tracking?.customer?.lng != null;
  const driverHasCoords =
    tracking?.driver?.lat != null && tracking?.driver?.lng != null;

  // Pick center: prefer driver, else customer, else Baghdad
  const center = driverHasCoords
    ? [tracking.driver.lat, tracking.driver.lng]
    : customerHasCoords
    ? [tracking.customer.lat, tracking.customer.lng]
    : [33.3152, 44.3661]; // Baghdad

  return (
    <div className="min-h-screen bg-[#FAFCFA]">
      <header className="sticky top-0 z-50 bg-white border-b border-[#E4EBE6]">
        <div className="max-w-md mx-auto md:max-w-3xl px-4 py-3 flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            data-testid="tracking-back"
            className="p-2 hover:bg-[#F3F6F4] rounded-full"
          >
            <ArrowRight className="w-5 h-5 text-[#2D4A3E]" />
          </button>
          <h1 className="font-heading font-bold text-lg text-[#111814]">تتبّع الطلب</h1>
          <button
            onClick={fetchTracking}
            data-testid="tracking-refresh"
            className="mr-auto p-2 hover:bg-[#F3F6F4] rounded-full"
          >
            <RefreshCw className="w-5 h-5 text-[#2D4A3E]" />
          </button>
        </div>
      </header>

      <main className="max-w-md mx-auto md:max-w-3xl px-4 py-4 space-y-4">
        {/* Status banner */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          data-testid="tracking-status"
          className="bg-gradient-to-l from-[#3B82F6] to-[#1E3A5F] text-white rounded-2xl p-4"
        >
          <p className="text-xs opacity-90">حالة الطلب</p>
          <p className="font-bold text-xl">{statusLabels[tracking.status] || tracking.status}</p>
        </motion.div>

        {/* Map */}
        <div
          className="rounded-2xl overflow-hidden border border-[#E4EBE6] shadow-md"
          style={{ height: "60vh", minHeight: 380 }}
          data-testid="tracking-map"
        >
          <MapContainer
            center={center}
            zoom={14}
            style={{ height: "100%", width: "100%" }}
            scrollWheelZoom
          >
            <TileLayer
              attribution='&copy; OpenStreetMap'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {customerHasCoords && (
              <Marker position={[tracking.customer.lat, tracking.customer.lng]} icon={homeIcon}>
                <Popup>
                  <div dir="rtl" className="text-sm">
                    <p className="font-bold">موقعك</p>
                    <p>{tracking.customer.market_name || tracking.customer.name}</p>
                  </div>
                </Popup>
              </Marker>
            )}
            {driverHasCoords && (
              <Marker position={[tracking.driver.lat, tracking.driver.lng]} icon={carIcon}>
                <Popup>
                  <div dir="rtl" className="text-sm">
                    <p className="font-bold">{tracking.driver.name}</p>
                    {tracking.driver.vehicle_type && <p>{tracking.driver.vehicle_type}</p>}
                    {tracking.driver.phone && (
                      <a href={`tel:${tracking.driver.phone}`} className="text-[#E85D45]">{tracking.driver.phone}</a>
                    )}
                  </div>
                </Popup>
              </Marker>
            )}
            {/* Draw a dashed route between driver and customer when both visible */}
            {driverHasCoords && customerHasCoords && tracking.status !== "delivered" && (
              <Polyline
                positions={[
                  [tracking.driver.lat, tracking.driver.lng],
                  [tracking.customer.lat, tracking.customer.lng],
                ]}
                pathOptions={{ color: "#3B82F6", weight: 4, dashArray: "10 8", opacity: 0.8 }}
              />
            )}
          </MapContainer>
        </div>

        {/* Delivered banner — order completed */}
        {tracking.status === "delivered" && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            data-testid="tracking-delivered-banner"
            className="bg-gradient-to-l from-[#22C55E] to-[#16A34A] text-white rounded-2xl p-4 flex items-center gap-3 shadow-lg"
          >
            <CheckCircle2 className="w-10 h-10 shrink-0" />
            <div className="flex-1">
              <p className="font-black text-lg">تم الوصول بنجاح ✅</p>
              <p className="text-xs opacity-90">انتهت رحلة هذا الطلب — شكراً لاستخدامك جُملتي</p>
            </div>
            <button
              onClick={() => navigate("/my-orders")}
              data-testid="tracking-back-orders"
              className="bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-full text-xs font-bold shrink-0"
            >
              طلباتي
            </button>
          </motion.div>
        )}

        {/* Customer location set */}
        {!customerHasCoords && (
          <div className="bg-[#FFF8E1] border border-[#F5A623]/30 rounded-2xl p-4">
            <p className="text-sm text-[#111814] mb-2 flex items-center gap-1">
              <MapPin className="w-4 h-4 text-[#F5A623]" />
              لم نسجّل موقعك بعد. اضغط لتسجيل موقعك الحالي ليراه السائق:
            </p>
            <button
              onClick={captureMyLocation}
              disabled={savingLocation}
              data-testid="capture-my-location"
              className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white font-bold py-2.5 rounded-full disabled:opacity-50"
            >
              {savingLocation ? "جاري التحديد..." : "📍 حدّد موقعي الحالي"}
            </button>
          </div>
        )}

        {/* Driver info card */}
        {tracking.driver && (
          <div className="bg-white border border-[#E4EBE6] rounded-2xl p-4 flex items-center gap-3" data-testid="tracking-driver-card">
            {tracking.driver.photo_path ? (
              <img
                src={`${API}/files/${tracking.driver.photo_path}`}
                alt={tracking.driver.name}
                className="w-14 h-14 rounded-full object-cover border-2 border-[#3B82F6]"
              />
            ) : (
              <div className="w-14 h-14 rounded-full bg-[#F0F9FF] border-2 border-[#3B82F6] flex items-center justify-center">
                <Truck className="w-6 h-6 text-[#3B82F6]" />
              </div>
            )}
            <div className="flex-1">
              <p className="font-bold text-[#111814]">{tracking.driver.name}</p>
              <p className="text-xs text-[#5C6E64]">{tracking.driver.vehicle_type}</p>
              {tracking.driver.last_seen && (
                <p className="text-[10px] text-[#5C6E64]">آخر تحديث: {new Date(tracking.driver.last_seen).toLocaleTimeString("ar-IQ")}</p>
              )}
            </div>
            {tracking.driver.phone && (
              <a
                href={`tel:${tracking.driver.phone}`}
                data-testid="call-driver-btn"
                className="bg-[#7ED321] hover:bg-[#6cb91d] text-white p-3 rounded-full"
              >
                <Phone className="w-5 h-5" />
              </a>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default OrderTracking;
